import type {
  SafersCCTVResponse,
  SafersCCTV,
  CctvUpdateRequest,
  CctvPlaybackResponse,
  PtzPreset,
} from "./types/cctv.types";
import { API_BASE_URL } from "./config";

/** 응답 형태 검증 — data 배열이 존재하는지 확인 */
function isCCTVResponse(value: unknown): value is SafersCCTVResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "data" in value &&
    Array.isArray((value as SafersCCTVResponse).data)
  );
}

/** CCTV 목록 조회 (siteId로 현장별 필터링 가능) */
async function getCCTVs(siteId?: number): Promise<SafersCCTV[]> {
  const query = siteId != null ? `?siteId=${siteId}` : "";
  const res = await fetch(`${API_BASE_URL}/cctvs${query}`);
  if (!res.ok) throw new Error(`Failed to fetch CCTVs: ${res.status}`);
  const body: unknown = await res.json();
  if (!isCCTVResponse(body)) throw new Error("Invalid CCTV response shape");
  return body.data;
}

/** CCTV 수정 (이름, 경도, 위도, 고도) */
async function updateCCTV(id: number, data: CctvUpdateRequest): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update CCTV: ${res.status}`);
}

/** PTZ continuous 이동 시작 — pan/tilt/zoom 각 -1.0~1.0 속도. 정지는 ptzStop */
async function ptzMove(
  id: number,
  body: { pan?: number; tilt?: number; zoom?: number }
): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to move PTZ: ${res.status}`);
}

/** 진행 중인 continuous 이동(PanTilt·Zoom) 정지 */
async function ptzStop(id: number): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz/stop`, { method: "POST" });
  if (!res.ok) throw new Error(`Failed to stop PTZ: ${res.status}`);
}

/** PTZ 프리셋 목록 */
async function ptzPresets(id: number): Promise<PtzPreset[]> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz/presets`);
  if (!res.ok) throw new Error(`Failed to fetch presets: ${res.status}`);
  const body = (await res.json()) as { data?: PtzPreset[] };
  return Array.isArray(body.data) ? body.data : [];
}

/** 현재 위치를 프리셋으로 저장 */
async function ptzPresetSave(id: number, name: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz/presets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`Failed to save preset: ${res.status}`);
}

/** 프리셋 위치로 이동 */
async function ptzPresetGoto(id: number, token: string): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/cctvs/${id}/ptz/presets/${encodeURIComponent(token)}/goto`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`Failed to goto preset: ${res.status}`);
}

/** 프리셋 삭제 */
async function ptzPresetDelete(id: number, token: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${id}/ptz/presets/${encodeURIComponent(token)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to delete preset: ${res.status}`);
}

/** 미디어서버에서 CCTV 경로 목록을 가져와 DB에 동기화 */
async function syncCCTVs(siteId?: number): Promise<void> {
  const query = siteId != null ? `?siteId=${siteId}` : "";
  const res = await fetch(`${API_BASE_URL}/cctvs/sync${query}`, { method: "POST" });
  if (!res.ok) throw new Error(`Failed to sync CCTVs: ${res.status}`);
}

/**
 * WHEP URL 생성 — `/webrtc/{streamName}/whep`.
 *
 * 미디어 서버는 단일 base로 운영(현장별 포트 분기 없음).
 * dev에서는 vite proxy(`/webrtc` → VITE_MEDIA_SERVER_URL)가 origin을 우회시키고,
 * prod에서는 같은 origin이라 그대로 fetch.
 *
 * streamName은 `encodeURIComponent`로 인코딩 — 공백·특수문자에 안전.
 */
function getWHEPUrl(streamName: string): string {
  return `/webrtc/${encodeURIComponent(streamName)}/whep`;
}

/** NVR 녹화영상 재생 세션 생성 — pathName을 받아 WHEP URL 구성에 사용 */
async function requestPlayback(
  cctvId: number,
  startDate: string,
  endDate: string
): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/cctvs/${cctvId}/playback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ startDate, endDate }),
  });
  if (!res.ok) throw new Error(`Playback request failed: ${res.status}`);
  const body: CctvPlaybackResponse = await res.json();
  return body.data.pathName;
}

export const cctvService = {
  ptzMove,
  ptzStop,
  ptzPresets,
  ptzPresetSave,
  ptzPresetGoto,
  ptzPresetDelete,
  getCCTVs,
  updateCCTV,
  syncCCTVs,
  getWHEPUrl,
  requestPlayback,
};
